import {
  Capacitor,
  registerPlugin,
  type PluginListenerHandle,
} from '@capacitor/core';
import { challengeShareUrl } from './challengeApi';

interface GameCenterStatus {
  authenticated: boolean;
  activitySupported: boolean;
}

interface StartActivityResult {
  started: boolean;
  partyCode?: string;
  partyURL?: string;
  reason?: string;
}

interface SubmitResultResponse {
  submitted: boolean;
  reason?: string;
}

interface GameActivityPayload {
  code: string;
  partyCode?: string;
  partyURL?: string;
}

interface GameCenterBridgePlugin {
  getStatus(): Promise<GameCenterStatus>;
  startActivity(options: { code: string; targetDurationMs: number }): Promise<StartActivityResult>;
  submitResult(options: {
    code: string;
    durationMs: number;
    completed: boolean;
  }): Promise<SubmitResultResponse>;
  endActivity(): Promise<void>;
  addListener(
    eventName: 'gameActivityReceived',
    listener: (event: GameActivityPayload) => void,
  ): Promise<PluginListenerHandle>;
}

const GameCenterBridge = registerPlugin<GameCenterBridgePlugin>('GameCenterBridge');

function isNativeIOS(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

/**
 * Starts the App Store Connect Game Activity and returns Apple's party URL.
 * Until the Activity is approved (or on iOS < 26), the existing Universal Link
 * remains the source of truth so sharing never becomes blocked by Game Center.
 */
export async function gameCenterChallengeShareUrl(
  code: string,
  targetDurationMs: number,
): Promise<string> {
  const fallback = challengeShareUrl(code);
  if (!isNativeIOS()) return fallback;

  try {
    const result = await GameCenterBridge.startActivity({ code, targetDurationMs });
    return result.started && result.partyURL ? result.partyURL : fallback;
  } catch (error) {
    console.warn('[game-center] activity unavailable, using Universal Link', error);
    return fallback;
  }
}

/** Report only server-validated completed runs to the fastest-time leaderboard. */
export async function reportGameCenterChallengeResult(input: {
  code: string;
  durationMs: number;
  completed: boolean;
}): Promise<void> {
  if (!isNativeIOS()) return;
  try {
    await GameCenterBridge.submitResult(input);
  } catch (error) {
    console.warn('[game-center] leaderboard submission failed', error);
  }
}

export async function listenForGameCenterChallenges(
  onCode: (code: string) => void,
): Promise<() => void> {
  if (!isNativeIOS()) return () => {};
  try {
    const listener = await GameCenterBridge.addListener('gameActivityReceived', ({ code }) => {
      if (/^[23456789A-HJ-NP-Z]{6,12}$/.test(code)) onCode(code);
    });
    return () => { void listener.remove(); };
  } catch (error) {
    console.warn('[game-center] listener unavailable', error);
    return () => {};
  }
}
